/**
 * AlgoTrading Admin Panel
 * Routes: /admin/algotrading
 * Tabs: Stats, Bots, Subscriptions, Tiers, Deposits, Affiliate Network Ledger, Settings, Kill-Switch
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  BarChart3, Cpu, Award, IndianRupee, Users, Settings as SettingsIcon,
  AlertTriangle, ShieldX, Save, Pause, RefreshCw, Building2, Plus, Trash2
} from 'lucide-react';
import api from '../../utils/api';

const AlgoTradingAdmin = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [bots, setBots] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [settings, setSettings] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [depositSettings, setDepositSettings] = useState(null);
  const [cashDeposits, setCashDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  const TABS = [
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'smart-signals', label: '🎯 AI Smart Signals', icon: Cpu },
    { id: 'bots', label: 'User Bots', icon: Cpu },
    { id: 'subscriptions', label: 'Subscriptions', icon: Award },
    { id: 'tiers', label: 'Tiers', icon: IndianRupee },
    { id: 'deposits', label: 'TSB Deposits', icon: IndianRupee },
    { id: 'cash-deposits', label: '💵 Cash Deposits', icon: IndianRupee },
    { id: 'brokers', label: 'Brokers (Connected)', icon: Building2 },
    { id: 'broker-defs', label: '➕ Add Broker (Dynamic)', icon: Building2 },
    { id: 'market', label: 'Market Data', icon: BarChart3 },
    { id: 'mlm', label: 'Affiliate Network Ledger', icon: Users },
    { id: 'settings', label: 'Settings & Kill-Switch', icon: SettingsIcon },
  ];

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, b, c, d, e, f, g, h, ds, cd] = await Promise.all([
        api.get('/admin/algotrading/stats'),
        api.get('/admin/algotrading/bots'),
        api.get('/admin/algotrading/subscriptions'),
        api.get('/admin/algotrading/tiers'),
        api.get('/admin/algotrading/deposits'),
        api.get('/admin/algotrading/mlm/ledger'),
        api.get('/admin/algotrading/settings'),
        api.get('/admin/algotrading/brokers'),
        api.get('/admin/algotrading/deposit-settings'),
        api.get('/admin/algotrading/cash-deposits'),
      ]);
      setStats(a.data);
      setBots(b.data.bots || []);
      setSubs(c.data.subscriptions || []);
      setTiers(d.data.tiers || []);
      setDeposits(e.data.deposits || []);
      setLedger(f.data);
      setSettings(g.data);
      setBrokers(h.data.brokers || []);
      setDepositSettings(ds.data.settings || {});
      setCashDeposits(cd.data.deposits || []);
    } catch (err) {
      console.error('admin load failed', err);
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const reloadCashDeposits = async () => {
    try {
      const [ds, cd] = await Promise.all([
        api.get('/admin/algotrading/deposit-settings'),
        api.get('/admin/algotrading/cash-deposits'),
      ]);
      setDepositSettings(ds.data.settings || {});
      setCashDeposits(cd.data.deposits || []);
    } catch (err) { console.warn(err); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-12 px-4 bg-gradient-to-br from-slate-950 to-indigo-950 text-white" data-testid="algo-admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
            Anant Smart Trading Tools & Services — Admin
          </h1>
          <p className="text-slate-400 text-sm mt-1">Manage bots, subscriptions, AI signals, deposits, Affiliate Network payouts & kill-switch.</p>
        </div>

        {/* Kill-switch warning if active */}
        {settings?.kill_switch && (
          <div className="mb-4 bg-red-600/30 border-2 border-red-500 rounded-xl p-3 flex items-center gap-3" data-testid="admin-kill-banner">
            <ShieldX className="w-5 h-5 text-red-400" />
            <span className="text-red-200 text-sm">KILL-SWITCH ACTIVE — All bots halted. Reason: {settings.kill_switch_reason}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 sticky top-28 bg-slate-950/95 backdrop-blur z-30">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-algo-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${
                  activeTab === tab.id ? 'bg-amber-500 text-white' : 'bg-white/5 hover:bg-white/10'
                }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'stats' && <StatsView stats={stats} />}
        {activeTab === 'smart-signals' && <SmartSignalsAdminView />}
        {activeTab === 'broker-defs' && <BrokerDefsAdminView />}
        {activeTab === 'bots' && <BotsView bots={bots} reload={loadAll} />}
        {activeTab === 'subscriptions' && <SubsView subs={subs} />}
        {activeTab === 'tiers' && <TiersView tiers={tiers} reload={loadAll} />}
        {activeTab === 'deposits' && <DepositsView deposits={deposits} reload={loadAll} />}
        {activeTab === 'cash-deposits' && <CashDepositsView settings={depositSettings} deposits={cashDeposits} reload={reloadCashDeposits} />}
        {activeTab === 'brokers' && <BrokersView brokers={brokers} reload={loadAll} />}
        {activeTab === 'market' && <MarketDataView />}
        {activeTab === 'mlm' && <LedgerView ledger={ledger} />}
        {activeTab === 'settings' && <SettingsView settings={settings} reload={loadAll} />}
      </div>
    </div>
  );
};

const StatsView = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Active Bots', value: stats.total_active_bots, color: 'from-emerald-500 to-teal-500' },
    { label: 'Users w/ Bots', value: stats.total_users_with_bots, color: 'from-blue-500 to-indigo-500' },
    { label: 'Paper Trades', value: stats.total_paper_trades?.toLocaleString('en-IN'), color: 'from-amber-500 to-orange-500' },
    { label: 'Active Subs', value: stats.total_active_subscriptions, color: 'from-pink-500 to-rose-500' },
    { label: 'Pending Deposits', value: stats.pending_deposits, color: 'from-yellow-500 to-amber-500' },
    { label: 'Total Revenue', value: `₹${stats.total_revenue_inr?.toLocaleString('en-IN')}`, color: 'from-violet-500 to-purple-500' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-stats-grid">
      {cards.map((c, i) => (
        <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5">
          <div className={`text-xs uppercase tracking-wider bg-gradient-to-r ${c.color} bg-clip-text text-transparent font-semibold mb-2`}>{c.label}</div>
          <div className="text-3xl font-bold">{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const BotsView = ({ bots, reload }) => {
  const handleHalt = async (id) => {
    if (!window.confirm('Halt this bot?')) return;
    try { await api.post(`/admin/algotrading/bots/${id}/halt`); toast.success('Bot halted'); reload(); }
    catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-bots-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-xs uppercase text-slate-400">
          <tr><th className="p-3 text-left">User</th><th className="p-3 text-left">Strategy</th><th className="p-3">Mode</th><th className="p-3 text-right">Capital</th><th className="p-3 text-right">Trades</th><th className="p-3 text-right">P&L</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
        </thead>
        <tbody>
          {bots.map(b => (
            <tr key={b.id} className="border-t border-white/5">
              <td className="p-3"><div className="font-medium">{b.user_name || '—'}</div><div className="text-xs text-slate-400">{b.user_email}</div></td>
              <td className="p-3">{b.strategy_name}</td>
              <td className="p-3 text-center"><span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">{b.mode}</span></td>
              <td className="p-3 text-right">₹{b.capital_allocated?.toLocaleString('en-IN')}</td>
              <td className="p-3 text-right">{b.total_trades}</td>
              <td className={`p-3 text-right ${b.realized_pnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>₹{b.realized_pnl?.toFixed(2)}</td>
              <td className="p-3 text-center">
                {b.is_active ? <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">ACTIVE</span> : <span className="text-xs px-2 py-0.5 rounded bg-slate-500/20 text-slate-400">PAUSED</span>}
              </td>
              <td className="p-3 text-center">
                {b.is_active && (
                  <button onClick={() => handleHalt(b.id)} className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded text-xs" data-testid={`admin-halt-${b.id}`}>Halt</button>
                )}
              </td>
            </tr>
          ))}
          {bots.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-400">No bots yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const SubsView = ({ subs }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-subs-table">
    <table className="w-full text-sm">
      <thead className="bg-white/5 text-xs uppercase text-slate-400">
        <tr><th className="p-3 text-left">User</th><th className="p-3">Tier</th><th className="p-3 text-right">Price</th><th className="p-3">Started</th><th className="p-3">Expires</th><th className="p-3">Status</th></tr>
      </thead>
      <tbody>
        {subs.map(s => (
          <tr key={s.id} className="border-t border-white/5">
            <td className="p-3"><div className="font-medium">{s.user_name || '—'}</div><div className="text-xs text-slate-400">{s.user_email}</div></td>
            <td className="p-3">{s.tier_name}</td>
            <td className="p-3 text-right">₹{s.price_inr?.toLocaleString('en-IN')}</td>
            <td className="p-3 text-xs">{new Date(s.started_at).toLocaleDateString()}</td>
            <td className="p-3 text-xs">{new Date(s.expires_at).toLocaleDateString()}</td>
            <td className="p-3 text-center">
              {s.is_active && new Date(s.expires_at) > new Date()
                ? <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">ACTIVE</span>
                : <span className="text-xs px-2 py-0.5 rounded bg-slate-500/20 text-slate-400">EXPIRED</span>}
            </td>
          </tr>
        ))}
        {subs.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-400">No subscriptions yet</td></tr>}
      </tbody>
    </table>
  </div>
);

const TiersView = ({ tiers, reload }) => {
  const [editing, setEditing] = useState(null);
  const [price, setPrice] = useState('');
  const save = async (t) => {
    try { await api.put(`/admin/algotrading/tiers/${t.id}`, { price_inr: parseFloat(price) }); toast.success('Updated'); setEditing(null); reload(); }
    catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-tiers-grid">
      {tiers.map(t => (
        <div key={t.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
          <h4 className="font-bold mb-1">{t.name}</h4>
          {editing === t.id ? (
            <div className="space-y-2">
              <input type="number" defaultValue={t.price_inr} onChange={e => setPrice(e.target.value)} className="w-full bg-slate-800 border border-white/10 rounded px-2 py-1" />
              <div className="flex gap-2">
                <button onClick={() => save(t)} className="flex-1 py-1 bg-amber-500 text-white rounded text-sm" data-testid={`admin-tier-save-${t.id}`}>Save</button>
                <button onClick={() => setEditing(null)} className="flex-1 py-1 bg-white/10 rounded text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-2xl font-bold">₹{t.price_inr.toLocaleString('en-IN')}</div>
              <div className="text-xs text-slate-400 mb-2">{t.duration_days}d · {t.max_bots} bots · ₹{t.max_capital?.toLocaleString('en-IN')} cap</div>
              <button onClick={() => { setEditing(t.id); setPrice(t.price_inr); }} className="text-xs text-amber-400 hover:text-amber-300" data-testid={`admin-tier-edit-${t.id}`}>Edit price</button>
            </>
          )}
        </div>
      ))}
    </div>
  );
};


const CashDepositsView = ({ settings, deposits, reload }) => {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(null);

  useEffect(() => { setForm(settings || {}); }, [settings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/algotrading/deposit-settings', form);
      toast.success('Deposit account details saved ✓');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const decide = async (id, action, defaultNote) => {
    const note = window.prompt(`${action === 'approve' ? '✓ Approve' : '✕ Reject'} this deposit?\n\nOptional note for the user:`, defaultNote || '');
    if (note === null) return;
    setProcessing(id);
    try {
      await api.post(`/admin/algotrading/cash-deposits/${id}/${action}`, { admin_notes: note });
      toast.success(action === 'approve' ? 'Approved & cash credited ✓' : 'Rejected ✕');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally { setProcessing(null); }
  };

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const pending = deposits.filter(d => d.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Settings card */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="admin-deposit-settings">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-bold">Deposit Account Settings</h3>
            <p className="text-xs text-slate-400">These details are shown to users on the Auto-Trading → Deposit Cash tab.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active !== false} onChange={e => upd('is_active', e.target.checked)} data-testid="dep-active" />
            <span className={form.is_active !== false ? 'text-emerald-300' : 'text-red-300'}>
              {form.is_active !== false ? '🟢 Accepting deposits' : '🔴 Deposits paused'}
            </span>
          </label>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* UPI */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-amber-300 uppercase tracking-wider">UPI</h4>
            <Input label="UPI ID" value={form.upi_id || ''} onChange={v => upd('upi_id', v)} placeholder="anant@upi" testid="dep-upi-id" />
            <Input label="UPI QR Image URL (optional)" value={form.upi_qr_url || ''} onChange={v => upd('upi_qr_url', v)} placeholder="https://…/qr.png" testid="dep-upi-qr" />
          </div>
          {/* Bank */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-blue-300 uppercase tracking-wider">Bank</h4>
            <Input label="Account Holder Name" value={form.bank_account_name || ''} onChange={v => upd('bank_account_name', v)} placeholder="Anant Web Portal" testid="dep-bank-name" />
            <Input label="Account Number" value={form.bank_account_number || ''} onChange={v => upd('bank_account_number', v)} placeholder="50100123456789" testid="dep-bank-acc" />
            <Input label="IFSC Code" value={form.bank_ifsc || ''} onChange={v => upd('bank_ifsc', v)} placeholder="HDFC0000123" testid="dep-bank-ifsc" />
            <Input label="Bank Name" value={form.bank_name || ''} onChange={v => upd('bank_name', v)} placeholder="HDFC Bank" testid="dep-bank-branch" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <Input label="Min Amount (₹)" type="number" value={form.min_amount || ''} onChange={v => upd('min_amount', parseFloat(v) || 0)} placeholder="100" testid="dep-min" />
          <Input label="Max Amount (₹)" type="number" value={form.max_amount || ''} onChange={v => upd('max_amount', parseFloat(v) || 0)} placeholder="1000000" testid="dep-max" />
          <div className="flex items-end">
            <button onClick={saveSettings} disabled={saving} className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-lg disabled:opacity-50" data-testid="dep-save">
              {saving ? 'Saving…' : <span className="flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save Settings</span>}
            </button>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-slate-400 mb-1">Instructions shown to user</label>
          <textarea value={form.instructions || ''} onChange={e => upd('instructions', e.target.value)} rows={2}
            className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="dep-instructions" />
        </div>
      </div>

      {/* Pending deposits */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="admin-pending-deposits">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-bold flex items-center gap-2">
            Pending Cash Deposits
            {pending.length > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold">{pending.length}</span>}
          </h3>
          <button onClick={reload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs flex items-center gap-1.5" data-testid="dep-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {deposits.length === 0 ? (
          <div className="text-center text-slate-500 py-8 text-sm">No cash deposit requests yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-white/5 text-xs uppercase text-slate-400">
                <tr>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">User</th>
                  <th className="p-2 text-right">Amount</th>
                  <th className="p-2 text-center">Method</th>
                  <th className="p-2 text-left">Ref / Payer</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map(d => (
                  <tr key={d.id} className="border-t border-white/5" data-testid={`cash-dep-row-${d.id}`}>
                    <td className="p-2 text-xs">{new Date(d.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="p-2 text-xs">
                      <div className="font-semibold">{d.user_name || d.user_email || d.user_id}</div>
                      {d.user_email && <div className="text-[10px] text-slate-500">{d.user_email}</div>}
                    </td>
                    <td className="p-2 text-right font-bold text-emerald-300">₹{d.amount.toLocaleString('en-IN')}</td>
                    <td className="p-2 text-center text-xs uppercase">{d.method}</td>
                    <td className="p-2 text-xs">
                      <div className="font-mono">{d.txn_ref}</div>
                      {d.payer_name && <div className="text-[10px] text-slate-500">{d.payer_name}</div>}
                      {d.screenshot_url && <a href={d.screenshot_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 underline">screenshot ↗</a>}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        d.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                        d.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                        'bg-amber-500/20 text-amber-300'}`}>{d.status}</span>
                    </td>
                    <td className="p-2">
                      {d.status === 'pending' ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => decide(d.id, 'approve', 'Verified, credited.')} disabled={processing === d.id}
                            className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-200 rounded text-xs disabled:opacity-50"
                            data-testid={`dep-approve-${d.id}`}>
                            {processing === d.id ? '…' : '✓ Approve'}
                          </button>
                          <button onClick={() => decide(d.id, 'reject', 'Could not verify payment.')} disabled={processing === d.id}
                            className="px-2 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-200 rounded text-xs disabled:opacity-50"
                            data-testid={`dep-reject-${d.id}`}>
                            ✕ Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-500 text-right">{d.admin_notes || '—'}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, placeholder, type = 'text', testid }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
      data-testid={testid}
    />
  </div>
);


const DepositsView = ({ deposits, reload }) => {
  const action = async (id, act) => {
    try { await api.post('/admin/algotrading/deposits/action', { deposit_id: id, action: act }); toast.success(`Deposit ${act}d`); reload(); }
    catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-deposits-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-xs uppercase text-slate-400">
          <tr><th className="p-3 text-left">User</th><th className="p-3 text-right">Amount</th><th className="p-3">UTR/Ref</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3">Actions</th></tr>
        </thead>
        <tbody>
          {deposits.map(d => (
            <tr key={d.id} className="border-t border-white/5">
              <td className="p-3"><div className="font-medium">{d.user_name || '—'}</div><div className="text-xs text-slate-400">{d.user_email}</div></td>
              <td className="p-3 text-right font-bold">₹{d.amount_inr?.toLocaleString('en-IN')}</td>
              <td className="p-3 text-xs font-mono">{d.utr || '—'}</td>
              <td className="p-3 text-center">
                {d.status === 'pending' && <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300">PENDING</span>}
                {d.status === 'approved' && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">APPROVED</span>}
                {d.status === 'rejected' && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300">REJECTED</span>}
              </td>
              <td className="p-3 text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
              <td className="p-3 text-center">
                {d.status === 'pending' && (
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => action(d.id, 'approve')} className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded text-xs" data-testid={`approve-dep-${d.id}`}>Approve</button>
                    <button onClick={() => action(d.id, 'reject')} className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded text-xs" data-testid={`reject-dep-${d.id}`}>Reject</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {deposits.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-400">No deposit requests yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const LedgerView = ({ ledger }) => {
  if (!ledger) return null;
  return (
    <div className="space-y-4" data-testid="admin-mlm-ledger">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="text-xs text-emerald-300 uppercase">Paid to Users</div>
          <div className="text-2xl font-bold">₹{ledger.total_paid_to_users?.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="text-xs text-amber-300 uppercase">Orphan → Admin</div>
          <div className="text-2xl font-bold">₹{ledger.total_orphan_to_admin?.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <div className="text-xs text-blue-300 uppercase">Total Entries</div>
          <div className="text-2xl font-bold">{ledger.total_count}</div>
        </div>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-slate-400">
            <tr><th className="p-3">Buyer</th><th className="p-3">Upline</th><th className="p-3">Level</th><th className="p-3 text-right">Amount</th><th className="p-3">Source</th><th className="p-3">Date</th></tr>
          </thead>
          <tbody>
            {ledger.entries.map(e => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="p-3 text-xs font-mono">{e.buyer_user_id?.slice(0, 8)}…</td>
                <td className="p-3 text-xs font-mono">{e.upline_user_id?.slice(0, 8)}…</td>
                <td className="p-3 text-center"><span className={`text-xs px-2 py-0.5 rounded ${e.level === 1 ? 'bg-pink-500/20 text-pink-300' : 'bg-purple-500/20 text-purple-300'}`}>L{e.level} ({e.pct}%)</span></td>
                <td className="p-3 text-right font-bold">₹{e.amount_inr?.toFixed(2)}</td>
                <td className="p-3 text-xs">{e.source}</td>
                <td className="p-3 text-xs">{new Date(e.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SettingsView = ({ settings, reload }) => {
  const [form, setForm] = useState(settings || {});
  const [killReason, setKillReason] = useState('');

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const save = async () => {
    try {
      await api.put('/admin/algotrading/settings', {
        module_enabled: form.module_enabled,
        live_trading_enabled: form.live_trading_enabled,
        mlm_l1_pct: parseFloat(form.mlm_l1_pct),
        mlm_l2_pct: parseFloat(form.mlm_l2_pct),
        tsb_cash_split_pct: parseFloat(form.tsb_cash_split_pct),
        legal_disclaimer: form.legal_disclaimer,
      });
      toast.success('Settings saved');
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleKill = async () => {
    const newState = !form.kill_switch;
    if (newState && !killReason) { toast.error('Please enter a reason'); return; }
    try {
      await api.post('/admin/algotrading/kill-switch', { enabled: newState, reason: newState ? killReason : '' });
      toast.success(newState ? '🛑 KILL-SWITCH ACTIVATED' : '✅ Kill-switch deactivated');
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Kill-switch */}
      <div className="bg-red-500/10 border-2 border-red-500/30 rounded-2xl p-5" data-testid="admin-kill-card">
        <h3 className="text-xl font-bold mb-2 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-400" /> Emergency Kill-Switch</h3>
        <p className="text-sm text-slate-400 mb-3">Halts ALL active bots site-wide. Use during market emergencies.</p>
        {!form.kill_switch && (
          <input value={killReason} onChange={e => setKillReason(e.target.value)} placeholder="Reason (required)" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 mb-3" data-testid="kill-reason-input" />
        )}
        <button onClick={toggleKill} className={`px-4 py-2 rounded-lg font-bold ${form.kill_switch ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-600 hover:bg-red-700'} text-white`} data-testid="kill-toggle-btn">
          {form.kill_switch ? '✅ DEACTIVATE Kill-Switch' : '🛑 ACTIVATE Kill-Switch'}
        </button>
      </div>

      {/* General settings */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="admin-settings-form">
        <div className="grid md:grid-cols-2 gap-3">
          <Toggle label="Module Enabled" checked={!!form.module_enabled} onChange={v => setForm({ ...form, module_enabled: v })} testid="toggle-module" />
          <Toggle label="Live Trading Enabled (Phase 2)" checked={!!form.live_trading_enabled} onChange={v => setForm({ ...form, live_trading_enabled: v })} testid="toggle-live" />
          <NumberInput label="Affiliate Network L1 %" value={form.mlm_l1_pct} onChange={v => setForm({ ...form, mlm_l1_pct: v })} testid="input-mlm-l1" />
          <NumberInput label="Affiliate Network L2 %" value={form.mlm_l2_pct} onChange={v => setForm({ ...form, mlm_l2_pct: v })} testid="input-mlm-l2" />
          <NumberInput label="Cash split % (rest goes to TSB)" value={form.tsb_cash_split_pct} onChange={v => setForm({ ...form, tsb_cash_split_pct: v })} testid="input-cash-split" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Legal Disclaimer (SEBI)</label>
          <textarea rows={3} value={form.legal_disclaimer || ''} onChange={e => setForm({ ...form, legal_disclaimer: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="input-disclaimer" />
        </div>
        <button onClick={save} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold flex items-center gap-2" data-testid="save-settings-btn">
          <Save className="w-4 h-4" /> Save Settings
        </button>
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
    <span className="text-sm">{label}</span>
    <button onClick={() => onChange(!checked)} data-testid={testid} className={`w-12 h-6 rounded-full transition relative ${checked ? 'bg-amber-500' : 'bg-slate-600'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition ${checked ? 'translate-x-6' : ''}`} />
    </button>
  </div>
);
// ============ Market Data View ============
const MarketDataView = () => {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ api_key: '', api_secret: '', redirect_uri: '' });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/algotrading/market/status'),
        api.get('/admin/algotrading/market/settings'),
      ]);
      setStatus(a.data);
      setSettings(b.data);
      setForm(f => ({ ...f, redirect_uri: b.data.upstox.redirect_uri || f.redirect_uri }));
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const saveKeys = async () => {
    if (!form.api_key || !form.api_secret) { toast.error('API key + secret required'); return; }
    setLoading(true);
    try {
      await api.post('/admin/algotrading/market/upstox/save', form);
      toast.success('Upstox keys saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  const getLoginUrl = async () => {
    try {
      const r = await api.get('/admin/algotrading/market/upstox/login-url');
      window.open(r.data.login_url, '_blank', 'noopener,noreferrer,width=900,height=700');
      toast.info('Sign in on Upstox, then paste the `code` from redirect URL below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const exchangeCode = async () => {
    if (!code) { toast.error('Paste the OAuth code'); return; }
    setLoading(true);
    try {
      const r = await api.post('/admin/algotrading/market/upstox/exchange-code', { code });
      toast.success(`✓ Connected as ${r.data.user_name || r.data.user_id}`);
      setCode('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Upstox? You can reconnect anytime.')) return;
    try { await api.delete('/admin/algotrading/market/upstox/disconnect'); toast.success('Disconnected'); load(); } catch { toast.error('Failed'); }
  };
  const setProvider = async (p) => {
    try { await api.post('/admin/algotrading/market/provider', { provider: p }); toast.success(`Provider override: ${p || 'auto'}`); load(); } catch { toast.error('Failed'); }
  };

  if (!status || !settings) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="market-data-view">
      {/* Status card */}
      <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold">Active Provider Chain</h3>
            <p className="text-xs text-slate-400">Tries in order; falls back on empty/error.</p>
          </div>
          <span className="text-xs px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full" data-testid="market-primary-provider">Primary: <b>{status.primary}</b></span>
        </div>
        <div className="grid sm:grid-cols-3 gap-2 mt-4">
          {status.chain.map(c => (
            <div key={c.provider} className={`rounded-xl p-3 border ${c.configured ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-slate-800/50 border-white/10'}`} data-testid={`provider-card-${c.provider}`}>
              <div className="font-bold capitalize">{c.provider}</div>
              <div className="text-xs mt-1">{c.configured ? <span className="text-emerald-300">● Configured</span> : <span className="text-slate-500">○ Not configured</span>}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <span className="text-xs text-slate-400 self-center">Explicit override:</span>
          {['', 'upstox', 'kite', 'nse'].map(p => (
            <button key={p || 'auto'} onClick={() => setProvider(p)}
              className={`px-2 py-1 text-xs rounded ${(status.explicit_override || '') === p ? 'bg-amber-500 text-white' : 'bg-white/5 hover:bg-white/10 text-slate-300'}`}
              data-testid={`provider-set-${p || 'auto'}`}>{p || 'auto'}</button>
          ))}
        </div>
      </div>

      {/* Upstox setup */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="upstox-setup">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-bold">Upstox Setup</h3>
          {settings.upstox.has_access_token ? (
            <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-full">● Connected · since {settings.upstox.access_token_obtained_at?.slice(0, 16)}</span>
          ) : (
            <span className="text-xs px-2 py-1 bg-slate-500/20 text-slate-400 rounded-full">○ Not connected</span>
          )}
        </div>
        <p className="text-sm text-slate-400 mb-3">
          1. Get your API key + secret from <a href="https://account.upstox.com/developer/apps" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline">Upstox Developer Apps</a>.
          2. Save them below. 3. Click {`"Login"`} → sign in on Upstox → paste the `code` from the redirect URL.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">API Key {settings.upstox.api_key && <span className="text-emerald-400 ml-2 font-mono">{settings.upstox.api_key}</span>}</label>
            <input type="password" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder="Paste new key (leave empty to keep current)" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upstox-api-key" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">API Secret {settings.upstox.api_secret && <span className="text-emerald-400 ml-2 font-mono">{settings.upstox.api_secret}</span>}</label>
            <input type="password" value={form.api_secret} onChange={e => setForm({ ...form, api_secret: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="upstox-api-secret" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-400 mb-1">Redirect URI</label>
            <input value={form.redirect_uri} onChange={e => setForm({ ...form, redirect_uri: e.target.value })} placeholder="https://anantwebportal.com/api/algotrading/upstox/callback" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" data-testid="upstox-redirect-uri" />
            <p className="text-xs text-slate-500 mt-1">Must match exactly the URL registered in your Upstox app dashboard.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button onClick={saveKeys} disabled={loading} className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="upstox-save-keys">Save Keys</button>
          <button onClick={getLoginUrl} disabled={!settings.upstox.api_key && !form.api_key} className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="upstox-login-url">1. Open Upstox Login</button>
          {settings.upstox.has_access_token && (
            <button onClick={disconnect} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm" data-testid="upstox-disconnect">Disconnect</button>
          )}
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <input value={code} onChange={e => setCode(e.target.value)} placeholder="Paste `code` from Upstox redirect URL here…" className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" data-testid="upstox-code-input" />
          <button onClick={exchangeCode} disabled={!code || loading} className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="upstox-exchange-code">2. Exchange Code → Token</button>
        </div>
      </div>

      {/* Live test */}
      <LiveQuoteTest />
    </div>
  );
};

const LiveQuoteTest = () => {
  const [symbols, setSymbols] = useState('RELIANCE,TCS,INFY,HDFCBANK');
  const [chain, setChain] = useState(null);
  const [chainSymbol, setChainSymbol] = useState('NIFTY');
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLtp = async () => {
    setLoading(true); setQuotes(null);
    try {
      const r = await api.get(`/algotrading/market/ltp?symbols=${encodeURIComponent(symbols)}`);
      setQuotes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  const fetchChain = async () => {
    setLoading(true); setChain(null);
    try {
      const r = await api.get(`/algotrading/market/option-chain/${chainSymbol}`);
      setChain(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="live-quote-test">
      <h3 className="text-lg font-bold mb-3">Live Test</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Stock LTP (comma-separated symbols)</label>
          <div className="flex gap-2">
            <input value={symbols} onChange={e => setSymbols(e.target.value)} className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="ltp-symbols" />
            <button onClick={fetchLtp} disabled={loading} className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold" data-testid="ltp-fetch">Fetch</button>
          </div>
          {quotes && (
            <pre className="mt-2 text-xs bg-slate-900 rounded p-2 overflow-x-auto" data-testid="ltp-result">{JSON.stringify(quotes, null, 2)}</pre>
          )}
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">F&O Option Chain</label>
          <div className="flex gap-2">
            <select value={chainSymbol} onChange={e => setChainSymbol(e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg px-2 py-2 text-sm" data-testid="chain-symbol">
              {['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={fetchChain} disabled={loading} className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold" data-testid="chain-fetch">Fetch Chain</button>
          </div>
          {chain && (
            <div className="mt-2 text-xs bg-slate-900 rounded p-2" data-testid="chain-result">
              <div>Underlying: <b>{chain.underlying_value || '—'}</b> · Expiry: <b>{chain.expiry}</b> · Source: <b>{chain.source}</b></div>
              <div>Strikes: <b>{chain.instruments?.length || 0}</b></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const NumberInput = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <input type="number" step="0.1" value={value || 0} onChange={e => onChange(parseFloat(e.target.value))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid={testid} />
  </div>
);

// ============ Brokers View ============
const BrokersView = ({ brokers, reload }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ id: '', name: '', category: 'stocks', country: 'IN', auth: 'manual', is_enabled: true, notes: '' });

  const handleToggle = async (b) => {
    try {
      await api.patch(`/admin/algotrading/brokers/${b.id}/toggle`);
      toast.success(`${b.name} ${b.is_enabled ? 'disabled' : 'enabled'}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const handleDelete = async (b) => {
    if (b.is_default) {
      toast.error('Default brokers cannot be deleted — disable them instead.');
      return;
    }
    if (!window.confirm(`Delete broker "${b.name}"?`)) return;
    try {
      await api.delete(`/admin/algotrading/brokers/${b.id}`);
      toast.success('Broker deleted');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleSave = async () => {
    if (!form.id || !form.name) { toast.error('ID and Name are required'); return; }
    try {
      await api.post('/admin/algotrading/brokers', form);
      toast.success('Broker saved');
      setAdding(false);
      setForm({ id: '', name: '', category: 'stocks', country: 'IN', auth: 'manual', is_enabled: true, notes: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-brokers-view">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Manage which brokers users can connect from the API Vault. Defaults can be disabled but not deleted.</p>
        {!adding && (
          <button onClick={() => setAdding(true)} className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm flex items-center gap-2" data-testid="add-broker-btn">
            <Plus className="w-4 h-4" /> Add Broker
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3" data-testid="add-broker-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Broker ID (slug)</label>
              <input value={form.id} onChange={e => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 font-mono" placeholder="upstox" data-testid="broker-id" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Display Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" placeholder="Upstox" data-testid="broker-name" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="broker-category">
                <option value="stocks">Stocks</option>
                <option value="crypto">Crypto</option>
                <option value="forex">Forex</option>
                <option value="commodity">Commodity</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Country</label>
              <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" placeholder="IN / GLOBAL" data-testid="broker-country" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Auth Type</label>
              <select value={form.auth} onChange={e => setForm({ ...form, auth: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2" data-testid="broker-auth">
                <option value="manual">manual</option>
                <option value="hmac">hmac</option>
                <option value="hmac+passphrase">hmac+passphrase</option>
                <option value="oauth">oauth</option>
                <option value="totp+key">totp+key</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input type="checkbox" checked={form.is_enabled} onChange={e => setForm({ ...form, is_enabled: e.target.checked })} className="w-4 h-4" data-testid="broker-enabled" />
              Enabled
            </label>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Auth phase / requirements" data-testid="broker-notes" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAdding(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm" data-testid="broker-cancel">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold text-sm" data-testid="broker-save">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-brokers-table">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">ID</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Country</th>
              <th className="p-3 text-left">Auth</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {brokers.map(b => (
              <tr key={b.id} data-testid={`broker-row-${b.id}`}>
                <td className="p-3 font-semibold">{b.name}{b.is_default && <span className="ml-2 text-xs text-slate-500">(default)</span>}</td>
                <td className="p-3 font-mono text-xs">{b.id}</td>
                <td className="p-3 capitalize">{b.category}</td>
                <td className="p-3">{b.country || '—'}</td>
                <td className="p-3 text-xs">{b.auth}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.is_enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>
                    {b.is_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => handleToggle(b)} className="px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs" data-testid={`toggle-broker-${b.id}`}>
                      {b.is_enabled ? 'Disable' : 'Enable'}
                    </button>
                    {!b.is_default && (
                      <button onClick={() => handleDelete(b)} className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded text-xs" data-testid={`delete-broker-${b.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {brokers.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">No brokers configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AlgoTradingAdmin;

// ============ AI SMART SIGNALS — Admin CRUD View ============
const SmartSignalsAdminView = () => {
  const [categories, setCategories] = useState([]);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // signal object or null
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/admin/algotrading/smart-signals/categories'),
        api.get('/admin/algotrading/smart-signals', {
          params: {
            ...(filterCat !== 'all' ? { category: filterCat } : {}),
            ...(filterStatus !== 'all' ? { status: filterStatus.toUpperCase() } : {}),
          }
        }),
      ]);
      setCategories(c.data.categories || []);
      setSignals(s.data.signals || []);
      setStats(s.data.stats || null);
    } catch (err) {
      toast.error('Failed to load signals');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterCat, filterStatus]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this signal? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/algotrading/smart-signals/${id}`);
      toast.success('Signal deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleClose = async (id, result) => {
    try {
      await api.post(`/admin/algotrading/smart-signals/${id}/close?result=${result}`);
      toast.success(`Signal closed as ${result}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close');
    }
  };

  if (loading) return <div className="py-12 text-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="admin-smart-signals-view" className="space-y-5">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'from-slate-500 to-slate-600' },
            { label: 'Open', value: stats.open, color: 'from-emerald-500 to-teal-500' },
            { label: 'Closed', value: stats.closed, color: 'from-amber-500 to-orange-500' },
            { label: 'Success', value: stats.success, color: 'from-green-500 to-emerald-500' },
            { label: 'Win %', value: `${stats.win_rate_pct}%`, color: 'from-pink-500 to-rose-500' },
          ].map(c => (
            <div key={c.label} className={`bg-gradient-to-br ${c.color} rounded-xl p-3 text-white`}>
              <div className="text-[10px] uppercase opacity-80">{c.label}</div>
              <div className="text-xl font-extrabold">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex gap-2 flex-wrap items-center">
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            data-testid="admin-signal-filter-cat"
            className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            data-testid="admin-signal-filter-status"
            className="bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Status</option>
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="admin-signal-add-btn"
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center gap-2 hover:shadow-lg hover:shadow-amber-500/30 transition"
        >
          <Plus className="w-4 h-4" /> Add Signal
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <SignalFormModal
          editing={editing}
          categories={categories}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {/* List */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-slate-300">
              <th className="p-3">Title</th>
              <th className="p-3 hidden md:table-cell">Category</th>
              <th className="p-3">Symbol</th>
              <th className="p-3">Dir</th>
              <th className="p-3 hidden md:table-cell">TP / SL</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">No signals yet. Click <strong>Add Signal</strong> to create the first one.</td></tr>
            )}
            {signals.map(sig => {
              const cat = categories.find(c => c.id === sig.category);
              return (
                <tr key={sig.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-signal-row-${sig.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-white truncate max-w-[200px]">{sig.title}</div>
                    {sig.is_demo && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">DEMO</span>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-slate-300">{cat ? `${cat.icon} ${cat.name}` : sig.category}</td>
                  <td className="p-3 font-mono text-amber-300">{sig.symbol}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      sig.direction === 'BUY' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                    }`}>{sig.direction}</span>
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs">
                    <div className="text-emerald-300">TP: {sig.target_price}</div>
                    <div className="text-red-300">SL: {sig.stop_loss}</div>
                  </td>
                  <td className="p-3">
                    {sig.status === 'OPEN' ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 animate-pulse">● OPEN</span>
                    ) : (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        sig.result === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                      }`}>● {sig.result || 'CLOSED'}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      <button
                        onClick={() => { setEditing(sig); setShowForm(true); }}
                        data-testid={`admin-signal-edit-${sig.id}`}
                        className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs hover:bg-blue-500/30"
                      >Edit</button>
                      {sig.status === 'OPEN' && (
                        <>
                          <button
                            onClick={() => handleClose(sig.id, 'SUCCESS')}
                            data-testid={`admin-signal-close-success-${sig.id}`}
                            className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 text-xs hover:bg-emerald-500/30"
                          >✓ Success</button>
                          <button
                            onClick={() => handleClose(sig.id, 'FAIL')}
                            data-testid={`admin-signal-close-fail-${sig.id}`}
                            className="px-2 py-1 rounded bg-red-500/20 text-red-300 text-xs hover:bg-red-500/30"
                          >✗ Fail</button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(sig.id)}
                        data-testid={`admin-signal-delete-${sig.id}`}
                        className="px-2 py-1 rounded bg-red-500/20 text-red-300 text-xs hover:bg-red-500/30 flex items-center"
                      ><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SignalFormModal = ({ editing, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(editing || {
    category: categories[0]?.id || 'indian_stocks',
    title: '',
    symbol: '',
    direction: 'BUY',
    buy_sell_around: '',
    target_price: '',
    stop_loss: '',
    image_url: '',
    remarks: '',
    safety_disclaimer: 'Investments are subject to market risks. Do your own research. This is NOT financial advice.',
    status: 'OPEN',
    result: '',
    is_demo: false,
  });
  const [saving, setSaving] = useState(false);

  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.symbol.trim() || !form.buy_sell_around.trim() || !form.target_price.trim() || !form.stop_loss.trim()) {
      toast.error('Title, symbol, entry, target, SL are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.result) delete payload.result;
      if (editing) {
        await api.put(`/admin/algotrading/smart-signals/${editing.id}`, payload);
        toast.success('Signal updated');
      } else {
        await api.post('/admin/algotrading/smart-signals', payload);
        toast.success('Signal created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/70 backdrop-blur-sm p-3 overflow-y-auto" data-testid="admin-signal-form-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-bold text-lg text-white">{editing ? 'Edit Signal' : 'New Smart Signal'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="admin-signal-form-close">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Category">
              <select value={form.category} onChange={e => handle('category', e.target.value)} data-testid="signal-form-category" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm">
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </Field>
            <Field label="Direction">
              <select value={form.direction} onChange={e => handle('direction', e.target.value)} data-testid="signal-form-direction" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </Field>
            <Field label="Title">
              <input value={form.title} onChange={e => handle('title', e.target.value)} data-testid="signal-form-title" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="e.g. RELIANCE Breakout Setup" />
            </Field>
            <Field label="Symbol">
              <input value={form.symbol} onChange={e => handle('symbol', e.target.value)} data-testid="signal-form-symbol" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="e.g. RELIANCE" />
            </Field>
            <Field label="Buy/Sell Around (Entry)">
              <input value={form.buy_sell_around} onChange={e => handle('buy_sell_around', e.target.value)} data-testid="signal-form-entry" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="₹2,450 - ₹2,470" />
            </Field>
            <Field label="Target Price (TP)">
              <input value={form.target_price} onChange={e => handle('target_price', e.target.value)} data-testid="signal-form-tp" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="₹2,650" />
            </Field>
            <Field label="Stop Loss (SL)">
              <input value={form.stop_loss} onChange={e => handle('stop_loss', e.target.value)} data-testid="signal-form-sl" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="₹2,380" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={e => handle('status', e.target.value)} data-testid="signal-form-status" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="OPEN">OPEN</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </Field>
            {form.status === 'CLOSED' && (
              <Field label="Result">
                <select value={form.result || ''} onChange={e => handle('result', e.target.value)} data-testid="signal-form-result" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">— Select —</option>
                  <option value="SUCCESS">SUCCESS ✓</option>
                  <option value="FAIL">FAIL ✗</option>
                </select>
              </Field>
            )}
          </div>
          <Field label="Image URL (optional)">
            <input value={form.image_url || ''} onChange={e => handle('image_url', e.target.value)} data-testid="signal-form-image" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
          </Field>
          <Field label="Remarks">
            <textarea value={form.remarks || ''} onChange={e => handle('remarks', e.target.value)} data-testid="signal-form-remarks" rows={2} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Trade thesis / context…" />
          </Field>
          <Field label="Safety Disclaimer">
            <textarea value={form.safety_disclaimer || ''} onChange={e => handle('safety_disclaimer', e.target.value)} data-testid="signal-form-disclaimer" rows={2} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={!!form.is_demo} onChange={e => handle('is_demo', e.target.checked)} data-testid="signal-form-is-demo" className="rounded" />
            Mark as <strong>DEMO</strong> (visible to free / non-subscribed users as a preview)
          </label>
          <div className="flex gap-2 justify-end pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200">Cancel</button>
            <button type="submit" disabled={saving} data-testid="signal-form-save" className="px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold disabled:opacity-50 flex items-center gap-1">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : (editing ? 'Update' : 'Create Signal')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1 block">{label}</label>
    {children}
  </div>
);

// ============ DYNAMIC BROKERS — Admin CRUD ============
const BrokerDefsAdminView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', slug: '', category: 'equity', auth_type: 'api_key_secret',
    base_url: '', docs_url: '', logo_url: '', notes: '',
    supported_segments: ['NSE'], is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/algotrading/admin/broker-defs');
      setItems(r.data.brokers || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const begin = (b) => {
    setEditing(b);
    setForm({
      name: b.name, slug: b.slug, category: b.category, auth_type: b.auth_type,
      base_url: b.base_url || '', docs_url: b.docs_url || '', logo_url: b.logo_url || '',
      notes: b.notes || '', supported_segments: b.supported_segments || [], is_active: b.is_active,
    });
    setShowForm(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ name: '', slug: '', category: 'equity', auth_type: 'api_key_secret', base_url: '', docs_url: '', logo_url: '', notes: '', supported_segments: ['NSE'], is_active: true });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.slug.trim()) { toast.error('Name and Slug required'); return; }
    try {
      if (editing) {
        await api.put(`/algotrading/admin/broker-defs/${editing.id}`, form);
        toast.success('Broker updated');
      } else {
        await api.post('/algotrading/admin/broker-defs', form);
        toast.success('Broker added');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this broker definition?')) return;
    try { await api.delete(`/algotrading/admin/broker-defs/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  if (loading) return <div className="text-center py-12"><RefreshCw className="w-8 h-8 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="broker-defs-admin" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Dynamic Broker Definitions</h2>
          <p className="text-xs text-slate-400 mt-1">Add new brokers at runtime by category. Users see active ones in the Connect Broker flow.</p>
        </div>
        <button onClick={startNew} data-testid="broker-def-add-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold flex items-center gap-1"><Plus className="w-4 h-4" /> Add Broker</button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr className="text-left text-slate-300"><th className="p-3">Name</th><th className="p-3">Slug</th><th className="p-3">Category</th><th className="p-3">Segments</th><th className="p-3">Active</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No custom brokers yet. Click <strong>Add Broker</strong> to add one.</td></tr>}
            {items.map(b => (
              <tr key={b.id} className="border-t border-white/5" data-testid={`broker-def-row-${b.slug}`}>
                <td className="p-3 font-semibold text-white">{b.name}</td>
                <td className="p-3 font-mono text-xs text-amber-300">{b.slug}</td>
                <td className="p-3 capitalize">{b.category}</td>
                <td className="p-3 text-xs">{(b.supported_segments || []).join(', ')}</td>
                <td className="p-3">{b.is_active ? <span className="text-emerald-300">✓</span> : <span className="text-red-300">✗</span>}</td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <button onClick={() => begin(b)} className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">Edit</button>
                    <button onClick={() => remove(b.id)} className="px-2 py-1 rounded bg-red-500/20 text-red-300 text-xs"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm" onClick={() => setShowForm(false)} data-testid="broker-def-form">
          <div onClick={e => e.stopPropagation()} className="bg-slate-900 border border-white/10 rounded-2xl max-w-xl w-full p-5 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg text-white mb-3">{editing ? 'Edit Broker' : 'Add New Broker'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="broker-def-name" className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Slug (unique, lowercase)</label><input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} data-testid="broker-def-slug" disabled={!!editing} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Category</label><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm"><option value="equity">Equity</option><option value="crypto">Crypto</option><option value="forex">Forex</option><option value="commodity">Commodity</option><option value="futures">Futures</option></select></div>
              <div><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Auth Type</label><select value={form.auth_type} onChange={e => setForm(f => ({ ...f, auth_type: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm"><option value="api_key_secret">API Key + Secret</option><option value="oauth">OAuth</option><option value="api_key_only">API Key only</option></select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Base URL</label><input value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="https://api.broker.com" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Docs URL</label><input value={form.docs_url} onChange={e => setForm(f => ({ ...f, docs_url: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-400 mb-1 block">Supported Segments (comma)</label><input value={(form.supported_segments || []).join(', ')} onChange={e => setForm(f => ({ ...f, supported_segments: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="NSE, BSE, NFO" /></div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active (visible to users)</label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg bg-white/5">Cancel</button>
              <button onClick={save} data-testid="broker-def-save-btn" className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold flex items-center gap-1"><Save className="w-4 h-4" /> {editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


